import { UseFormReturn } from 'react-hook-form'
import { Divider } from 'utoniq-ui'

import {
  Gender,
  Maybe,
  PaymentDetailFragment,
  PersonalInfoFragment,
} from '@/clients/utoniq-core/schema'
import { tLabel } from '@/core'
import { Organism, OrganismFrame } from '@/core/architecture'
import { PostalCode } from '@/core/entities/values/PostalCode'
import { UseFormProps } from '@/core/form'
import {
  validateBirthdayConditional,
  validateCityConditional,
  validateFullNameConditional,
  validateLine1Conditional,
  validateLine2Conditional,
  validatePhoneNumberConditional,
  validatePostalCodeConditional,
  validatePrefectureConditional,
} from '@/core/validators'

import {
  AddressInputCell,
  BankAccountCell,
  BirthdayInputCell,
  ButtonCell,
  GenderInputCell,
  PaymentMethodCell,
  PhoneNumberInputCell,
  TextInputCell,
  UserAccountCell,
} from './components'

export type PersonalInfoFormProps = {
  user?: Maybe<PersonalInfoFragment>
  isMembershipPersonalInformationRequired: boolean
  visibleMoreInfo?: boolean
  paymentSource: Maybe<PaymentDetailFragment>
  submitting: boolean
  personalInfoForm: UseFormReturn<SettingValues>
  onSubmit: (value: SettingValues) => Promise<void | boolean>
  sendingSMSCode: boolean
  verifyingSMSCode: boolean
  needVerifyPhoneNumber: boolean
  onVerificationPhoneNumber: (values: { code: string }) => Promise<void>
  onChangeNeedVerifyPhoneNumber: (needVerifyPhoneNumber: boolean) => void
  onSendVerificationSMS: () => Promise<void>
  onResendVerificationSMS: () => Promise<void>
}

export type SettingValues = {
  fullName: string
  phoneNumber: string
  birthday: Date | null
  gender: Gender | null
  postalCode: string
  prefecture: string
  city: string
  line1: string
  line2: string
  isMembershipPersonalInformationRequired: boolean
  hasFullName: boolean
  hasPhoneNumber: boolean
  hasBirthday: boolean
  hasGender: boolean
  hasPostalCode: boolean
  hasPrefecture: boolean
  hasCity: boolean
  hasLine1: boolean
}

export const schema: UseFormProps['schema'] = o => ({
  fullName: validateFullNameConditional(
    o,
    {
      field: 'isMembershipPersonalInformationRequired',
      value: true,
      labelKey: 'personalInfo.fullName',
    },
    'hasFullName'
  ),
  phoneNumber: validatePhoneNumberConditional(
    o,
    {
      field: 'isMembershipPersonalInformationRequired',
      value: true,
      labelKey: 'personalInfo.phoneNumber',
    },
    'hasPhoneNumber'
  ),
  gender: o.string().when('isMembershipPersonalInformationRequired', {
    is: true,
    then: o.string().label(tLabel('personalInfo.gender')),
    otherwise: o
      .string()
      .optional()
      .allow(null)
      .label(tLabel('personalInfo.gender')),
  }),
  birthday: validateBirthdayConditional(
    o,
    {
      field: 'isMembershipPersonalInformationRequired',
      value: true,
      labelKey: 'personalInfo.birthday',
    },
    'hasBirthday'
  ),
  postalCode: validatePostalCodeConditional(
    o,
    {
      field: 'isMembershipPersonalInformationRequired',
      value: true,
      labelKey: 'personalInfo.postalCode',
    },
    'hasPostalCode'
  ),
  prefecture: validatePrefectureConditional(
    o,
    {
      field: 'isMembershipPersonalInformationRequired',
      value: true,
      labelKey: 'personalInfo.prefecture',
    },
    'hasPrefecture'
  ),
  city: validateCityConditional(
    o,
    {
      field: 'isMembershipPersonalInformationRequired',
      value: true,
      labelKey: 'personalInfo.city',
    },
    'hasCity'
  ),
  line1: validateLine1Conditional(
    o,
    {
      field: 'isMembershipPersonalInformationRequired',
      value: true,
      labelKey: 'personalInfo.line1',
    },
    'hasLine1'
  ),
  line2: validateLine2Conditional(o, {
    field: 'isMembershipPersonalInformationRequired',
    value: true,
    labelKey: 'personalInfo.line2',
  }),
  isMembershipPersonalInformationRequired: o.boolean(),
  hasFullName: o.boolean().optional(),
  hasPhoneNumber: o.boolean().optional(),
  hasBirthday: o.boolean().optional(),
  hasGender: o.boolean().optional(),
  hasPostalCode: o.boolean().optional(),
  hasPrefecture: o.boolean().optional(),
  hasCity: o.boolean().optional(),
  hasLine1: o.boolean().optional(),
})

export const PersonalInfoForm: Organism<PersonalInfoFormProps> = ({
  submitting,
  user,
  visibleMoreInfo,
  paymentSource,
  isMembershipPersonalInformationRequired,
  onSubmit,
  personalInfoForm,
  sendingSMSCode,
  verifyingSMSCode,
  needVerifyPhoneNumber,
  onChangeNeedVerifyPhoneNumber,
  onVerificationPhoneNumber,
  onSendVerificationSMS,
  onResendVerificationSMS,
}) => {
  const {
    register,
    formState: { errors, isValid, isSubmitting },
    watch,
    setValue,
    handleSubmit,
  } = personalInfoForm

  const onSubmitForm = handleSubmit(values => {
    const normalizedValues = {
      ...values,
      postalCode: PostalCode.normalize(values.postalCode),
    }

    return onSubmit(normalizedValues)
  })

  return (
    <OrganismFrame sx={{ pt: 3 }}>
      <TextInputCell
        type="text"
        {...register('fullName')}
        error={errors?.fullName}
        isRequired={isMembershipPersonalInformationRequired}
      />
      <Divider />
      <BirthdayInputCell
        value={watch('birthday')}
        setValue={setValue}
        error={errors?.birthday}
        isRequired={isMembershipPersonalInformationRequired}
      />
      <Divider />
      <GenderInputCell
        value={watch('gender')}
        setValue={setValue}
        isRequired={isMembershipPersonalInformationRequired}
      />
      {visibleMoreInfo && (
        <>
          <Divider />
          <UserAccountCell user={user} />
        </>
      )}
      <Divider />
      <PhoneNumberInputCell
        isRequired={isMembershipPersonalInformationRequired}
        isPhoneNumberVerified={!!user?.isPhoneNumberVerified}
        formattedPhoneNumber={user?.formattedPhoneNumber ?? ''}
        inputProps={{ ...register('phoneNumber'), error: errors.phoneNumber }}
        phoneNumber={watch('phoneNumber')}
        sendingSMSCode={sendingSMSCode}
        verifyingSMSCode={verifyingSMSCode}
        needVerifyPhoneNumber={needVerifyPhoneNumber}
        onChangeNeedVerifyPhoneNumber={onChangeNeedVerifyPhoneNumber}
        onVerificationPhoneNumber={onVerificationPhoneNumber}
        onSendVerificationSMS={onSendVerificationSMS}
        onResendVerificationSMS={onResendVerificationSMS}
      />
      <Divider />
      <AddressInputCell
        register={register}
        errors={errors}
        isRequired={isMembershipPersonalInformationRequired}
      />
      {visibleMoreInfo && (
        <>
          <Divider />
          <PaymentMethodCell paymentSource={paymentSource} />
          <Divider />
          <BankAccountCell bank={user?.bank} />
        </>
      )}
      <ButtonCell
        disabled={
          isMembershipPersonalInformationRequired
            ? !isValid || isSubmitting
            : isSubmitting
        }
        loading={isSubmitting || submitting}
        onSubmit={onSubmitForm}
      />
    </OrganismFrame>
  )
}

export const defaultValues: SettingValues = {
  gender: null,
  birthday: null,
  fullName: '',
  phoneNumber: '',
  postalCode: '',
  prefecture: '',
  city: '',
  line1: '',
  line2: '',
  isMembershipPersonalInformationRequired: false,
  hasFullName: false,
  hasPhoneNumber: false,
  hasBirthday: false,
  hasGender: false,
  hasPostalCode: false,
  hasPrefecture: false,
  hasCity: false,
  hasLine1: false,
}
